import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Tabs,
    Tab,
    Chip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Stack,
    Divider,
} from "@mui/material";
import {
    Refresh,
    Visibility,
    Delete,
    Email,
    Phone,
    Reply,
    Archive,
    MarkEmailRead,
    PersonSearch,
} from "@mui/icons-material";
import { GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import { ServerSideDataTable } from "../../components/common/DataTable";
import {
    contactMessageService,
    ContactMessage,
} from "../../services/contactMessageService";
import { formatDateShort } from "../../utils/date";

const STATUS_TABS = ["ALL", "NEW", "READ", "REPLIED", "ARCHIVED"] as const;
type StatusTab = (typeof STATUS_TABS)[number];

const SUBJECT_LABELS: Record<string, string> = {
    order: "Order Inquiry",
    product: "Product Question",
    return: "Return / Refund",
    account: "Account / Password Help",
    feedback: "Feedback",
    other: "Other",
};

const statusColor = (
    status: ContactMessage["status"],
): "warning" | "info" | "success" | "default" => {
    switch (status) {
        case "NEW":
            return "warning";
        case "READ":
            return "info";
        case "REPLIED":
            return "success";
        default:
            return "default";
    }
};

export const ContactMessages: React.FC = () => {
    const navigate = useNavigate();
    const [messages, setMessages] = useState<ContactMessage[]>([]);
    const [total, setTotal] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState<StatusTab>("ALL");
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [selected, setSelected] = useState<ContactMessage | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const loadMessages = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const result = await contactMessageService.getMessages({
                skip: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize,
                status: activeTab === "ALL" ? undefined : activeTab,
            });
            setMessages(result.items);
            setTotal(result.total);
            setUnreadCount(result.unread_count);
        } catch (err) {
            console.error(err);
            setError("Failed to load contact messages");
        } finally {
            setLoading(false);
        }
    }, [activeTab, paginationModel]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    const handleView = async (row: ContactMessage) => {
        setSelected(row);
        // Opening a message implies reading it — mirrors a normal inbox.
        // Never downgrades an already-actioned message back to READ.
        if (row.status === "NEW") {
            try {
                const updated = await contactMessageService.updateStatus(
                    row.message_id,
                    "READ",
                );
                setSelected(updated);
                setMessages((prev) =>
                    prev.map((m) =>
                        m.message_id === updated.message_id ? updated : m,
                    ),
                );
                setUnreadCount((c) => Math.max(0, c - 1));
            } catch (err) {
                console.error("Failed to mark message as read", err);
            }
        }
    };

    const handleStatusChange = async (
        newStatus: ContactMessage["status"],
    ) => {
        if (!selected) return;
        try {
            setActionLoading(true);
            const updated = await contactMessageService.updateStatus(
                selected.message_id,
                newStatus,
            );
            setSelected(updated);
            setMessages((prev) =>
                prev.map((m) =>
                    m.message_id === updated.message_id ? updated : m,
                ),
            );
            if (selected.status === "NEW" && newStatus !== "NEW") {
                setUnreadCount((c) => Math.max(0, c - 1));
            }
        } catch (err) {
            console.error(err);
            setError("Failed to update message status");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        if (!window.confirm(`Delete the message from "${selected.name}"?`))
            return;
        try {
            setActionLoading(true);
            await contactMessageService.deleteMessage(selected.message_id);
            setSelected(null);
            await loadMessages();
        } catch (err) {
            console.error(err);
            setError("Failed to delete message");
        } finally {
            setActionLoading(false);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "create_time",
            headerName: "Received",
            width: 130,
            valueFormatter: (value) => formatDateShort(value as string),
        },
        { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
        { field: "email", headerName: "Email", flex: 1.2, minWidth: 180 },
        {
            field: "subject",
            headerName: "Subject",
            width: 150,
            valueFormatter: (value) =>
                SUBJECT_LABELS[value as string] || (value as string),
        },
        {
            field: "message",
            headerName: "Message",
            flex: 2,
            minWidth: 240,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={params.value}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            width: 110,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={statusColor(params.value)}
                />
            ),
        },
        {
            field: "actions",
            type: "actions",
            headerName: "",
            width: 60,
            getActions: (params) => [
                <GridActionsCellItem
                    key="view"
                    icon={
                        <Tooltip title="View">
                            <Visibility fontSize="small" />
                        </Tooltip>
                    }
                    label="View"
                    onClick={() => handleView(params.row)}
                />,
            ],
        },
    ];

    const tabCounts = STATUS_TABS.map((tab) => {
        if (tab === "ALL") return undefined;
        if (tab === "NEW") return unreadCount;
        return messages.filter((m) => m.status === tab).length;
    });

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Contact Messages
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Messages submitted through the storefront&apos;s
                        Contact page
                    </Typography>
                </Box>
                <Button
                    startIcon={<Refresh />}
                    onClick={loadMessages}
                    variant="outlined"
                    size="small"
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError("")}
                >
                    {error}
                </Alert>
            )}

            <Tabs
                value={activeTab}
                onChange={(_, v) => {
                    setActiveTab(v);
                    setPaginationModel((p) => ({ ...p, page: 0 }));
                }}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
                {STATUS_TABS.map((tab, i) => (
                    <Tab
                        key={tab}
                        value={tab}
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                {tab}
                                {tabCounts[i] !== undefined &&
                                    tabCounts[i]! > 0 && (
                                        <Chip
                                            label={tabCounts[i]}
                                            size="small"
                                            color={
                                                tab === "NEW"
                                                    ? "warning"
                                                    : "default"
                                            }
                                        />
                                    )}
                            </Box>
                        }
                    />
                ))}
            </Tabs>

            <ServerSideDataTable
                rows={messages}
                columns={columns}
                loading={loading}
                hideActions
                getRowId={(row: ContactMessage) => row.message_id}
                totalRows={total}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
            />

            {/* Detail dialog */}
            <Dialog
                open={!!selected}
                onClose={() => setSelected(null)}
                maxWidth="sm"
                fullWidth
            >
                {selected && (
                    <>
                        <DialogTitle>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <span>{selected.name}</span>
                                <Chip
                                    label={selected.status}
                                    size="small"
                                    color={statusColor(selected.status)}
                                />
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Stack spacing={1.5} sx={{ mb: 2 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Email fontSize="small" color="action" />
                                    <Typography variant="body2">
                                        {selected.email}
                                    </Typography>
                                </Box>
                                {selected.phone && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Phone fontSize="small" color="action" />
                                        <Typography variant="body2">
                                            {selected.phone}
                                        </Typography>
                                    </Box>
                                )}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {SUBJECT_LABELS[selected.subject] ||
                                        selected.subject}{" "}
                                    &middot;{" "}
                                    {formatDateShort(selected.create_time)}
                                </Typography>
                            </Stack>
                            <Divider sx={{ mb: 2 }} />
                            <Typography
                                variant="body1"
                                sx={{ whiteSpace: "pre-wrap" }}
                            >
                                {selected.message}
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                            <Button
                                color="error"
                                startIcon={<Delete />}
                                onClick={handleDelete}
                                disabled={actionLoading}
                                sx={{ mr: "auto" }}
                            >
                                Delete
                            </Button>
                            {selected.status !== "ARCHIVED" && (
                                <Button
                                    startIcon={<Archive />}
                                    onClick={() =>
                                        handleStatusChange("ARCHIVED")
                                    }
                                    disabled={actionLoading}
                                >
                                    Archive
                                </Button>
                            )}
                            {selected.status !== "REPLIED" && (
                                <Button
                                    startIcon={<MarkEmailRead />}
                                    onClick={() =>
                                        handleStatusChange("REPLIED")
                                    }
                                    disabled={actionLoading}
                                >
                                    Mark Replied
                                </Button>
                            )}
                            {(selected.phone || selected.email) && (
                                <Button
                                    startIcon={<PersonSearch />}
                                    onClick={() =>
                                        navigate(
                                            `/customers?search=${encodeURIComponent(
                                                selected.phone || selected.email,
                                            )}`,
                                        )
                                    }
                                >
                                    Find in Customers
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<Reply />}
                                href={`mailto:${selected.email}?subject=${encodeURIComponent(
                                    `Re: ${SUBJECT_LABELS[selected.subject] || selected.subject}`,
                                )}`}
                            >
                                Reply via Email
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};
